import {
  Box,
  Grid,
  GridProps,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import { green, grey } from "@mui/material/colors";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext } from "react";
import { BentoBrand, UpgradeDialogSource } from "schemas/dashboard";

import { accessToPartnershipData } from "utils/influencer";

import { getVerifiedPartnershipData } from "../../helpers";

const styles = {
  checkActive: {
    color: green[500],
    minWidth: "24px",
  },
  upgradeLink: {
    fontSize: 12,
  },
  checkPaywall: {
    color: grey[400],
    minWidth: "24px",
  },
  paywallText: {
    fontStyle: "italic",
    color: grey[400],
  },
  noDataText: {
    fontStyle: "italic",
    color: grey[400],
    fontSize: 12,
  },
};

const PAYWALL_PREVIEW_OPTIONS = [
  "Offers paid collaborations with creators",
  "Sends gifted products or services to creators",
  "Last discussed a partnership with a creator 8 days ago",
  "Has discussed partnerships with creators in the United States and Canada",
];

function PartnershipData({
  bentoBrand,
  title,
  noDataHide = false,
  showGrid = false,
  gridProps = {},
}: {
  bentoBrand: BentoBrand;
  title?: string;
  noDataHide?: boolean;
  showGrid?: boolean;
  gridProps?: GridProps;
}) {
  const { currentOrg } = useContext(OrganizationUserContext);
  const { setUpgradeDialogSource, subscription } =
    useContext(SubscriptionContext);

  const hasAccessToPartnershipData = accessToPartnershipData(
    currentOrg,
    subscription,
  );

  if (noDataHide && !hasAccessToPartnershipData) {
    return <></>;
  }

  if (!hasAccessToPartnershipData) {
    return (
      <>
        <Typography gutterBottom>
          <strong>Partnership Data</strong>
        </Typography>
        <Typography gutterBottom>
          <Link
            component="button"
            sx={styles.upgradeLink}
            onClick={() => {
              setUpgradeDialogSource(
                UpgradeDialogSource.access_partnership_data,
              );
            }}
          >
            <Box
              component="span"
              className="fa-solid fa-lock-keyhole"
              sx={{ mr: 1 }}
            />
            <strong>Upgrade For:</strong>
          </Link>
        </Typography>
        <List dense disablePadding>
          {PAYWALL_PREVIEW_OPTIONS.map((paywallOption) => (
            <ListItem disablePadding key={paywallOption}>
              <ListItemIcon sx={styles.checkPaywall}>
                <Box component="span" className="fa-regular fa-circle-check" />
              </ListItemIcon>
              <ListItemText
                primaryTypographyProps={{
                  sx: styles.paywallText,
                }}
                primary={paywallOption}
              />
            </ListItem>
          ))}
        </List>
      </>
    );
  }

  const verifiedInformation = getVerifiedPartnershipData(bentoBrand);

  if (bentoBrand.partnerships && bentoBrand.partnerships.length > 0) {
    const latestPartnershipUpdatedAt = bentoBrand.partnerships[0].updated_at;
    const now = Date.now() / 1000;
    const daysAgo = Math.floor((now - latestPartnershipUpdatedAt) / 86400);

    const formattedText = `${daysAgo} day${daysAgo === 1 ? "" : "s"} ago`;

    verifiedInformation.push(
      `Last discussed a partnership with a creator ${formattedText}`,
    );

    const allCountryCodes = bentoBrand.partnerships
      .filter((p) => !!p.country)
      .map((p) => p.country);

    const uniqueCountryCodes = [...new Set(allCountryCodes)];

    let countrySummary;
    if (uniqueCountryCodes.length === 0) {
      countrySummary = null;
    } else if (uniqueCountryCodes.length <= 3) {
      countrySummary = uniqueCountryCodes.join(", ");
    } else {
      const shown = uniqueCountryCodes.slice(0, 3).join(", ");
      countrySummary = `${shown}, and more`;
    }

    if (countrySummary) {
      verifiedInformation.push(
        `Has discussed partnerships with creators in ${countrySummary}`,
      );
    }
  }

  if (noDataHide && verifiedInformation.length === 0) {
    return <></>;
  }

  const content = (
    <>
      <Typography gutterBottom>
        <strong>{title || "Partnership Data"}</strong>
      </Typography>
      <List dense disablePadding>
        {verifiedInformation.map((verifiedText) => (
          <ListItem key={verifiedText} disablePadding>
            <ListItemIcon sx={styles.checkActive}>
              <Box component="span" className="fa-regular fa-circle-check" />
            </ListItemIcon>
            <ListItemText primary={verifiedText} />
          </ListItem>
        ))}
        {verifiedInformation.length === 0 && (
          <Typography sx={styles.noDataText}>
            No data available for this brand
          </Typography>
        )}
      </List>
    </>
  );

  if (showGrid) {
    return (
      <Grid item {...gridProps}>
        {content}
      </Grid>
    );
  } else {
    return content;
  }
}

export default PartnershipData;
